/**
 * @fileoverview Hook exposing the current environment's physical Streamline database
 * names for the two logical legacy databases, so UI labels/dropdowns can show which
 * database will actually be queried without waiting for a live SQL fetch.
 * @module system-web/hooks/useLegacyDbOptions
 *
 * Responsibilities:
 * - Fetch GET /api/legacy/databases once on mount.
 * - Expose a map of logical database key -> resolved physical database name (or null when
 *   unavailable in this environment), plus the server's appEnv.
 *
 * Dependencies:
 * - ../services/legacyMigrationService (listLegacyDatabases)
 *
 * Architecture:
 * - Online-exception, one-shot fetch (not local-first) — this is UI metadata about which
 *   physical database the CURRENT server process will connect to, not tenant data, so it
 *   isn't a candidate for Dexie/liveQuery. Mirrors the pattern used by fetchDatasets() in
 *   legacyMigrationService.ts for the same reason.
 *
 * Notes:
 * - Used by DepartmentInfoStep (onboarding wizard dropdown) and LegacyMigrationPage
 *   (department sidebar + header) so both surfaces render the same physical-name mapping.
 */

import { useEffect, useState } from 'react';
import { listLegacyDatabases } from '../services/legacyMigrationService';

/**
 * @contract LegacyDbOptionsState
 * Purpose: Shape returned by useLegacyDbOptions.
 * Fields:  physicalByLogical (e.g. { SLInspectionsDB001: 'Dev_SLInspections001',
 *          SLInspectionsDB002: null }), appEnv, isLoading, error.
 * Rules:   physicalByLogical[key] is null when this environment has no copy of that database.
 * Sync:    Fetched once per mount; not cached across page loads.
 */
export interface LegacyDbOptionsState {
  physicalByLogical: Record<string, string | null>;
  appEnv: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * @hook useLegacyDbOptions
 *
 * Purpose:
 * - Provide the logical -> physical Streamline database name mapping for the current
 *   server environment, for display in dropdowns and labels.
 *
 * Returns:
 * - physicalByLogical, appEnv, isLoading, error
 *
 * Side Effects:
 * - Fires GET /api/legacy/databases once on mount.
 *
 * Offline Behavior:
 * - Not offline-capable (online exception) — falls back to an empty map, so callers should
 *   render the logical name alone when physicalByLogical[key] is undefined/null.
 */
export function useLegacyDbOptions(): LegacyDbOptionsState {
  const [physicalByLogical, setPhysicalByLogical] = useState<Record<string, string | null>>({});
  const [appEnv, setAppEnv] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listLegacyDatabases()
      .then((result) => {
        if (cancelled) return;
        const options: Array<{ logical: string; physical: string | null }> = result.options ?? [];
        setPhysicalByLogical(Object.fromEntries(options.map((o) => [o.logical, o.physical])));
        setAppEnv(result.appEnv ?? null);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn('[useLegacyDbOptions] Failed to fetch legacy database options:', err);
        setError(err instanceof Error ? err.message : 'Failed to load legacy database options');
        setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return { physicalByLogical, appEnv, isLoading, error };
}
